// Internal-call recording/transcription consent and capture control (B1 contract, frozen):
// GET /calls/:id/media -> CallCapture; POST .../media/consent; POST .../media/capture (202).
// The server is the authority. This module never infers that audio stopped or started from an
// accepted request: claims come only from `observed_state` and a confirmed `privacy_ack`.
import type { AgentControl, Call, CallCapture } from '@bcio-voice/contracts';
import { type ApiClient, type DataEnvelope, isAbortError, isApiRequestError } from '../lib/api.ts';
import { transcriptStateText, captureArchiveText, recorderObservation, transcriptsOnly, TRANSCRIPTS_ONLY_TEXT } from './mediaStatus.ts';
import type { MediaAccess } from '../state/mediaAccess.ts';

// ---- Consent model wording (docs/implementation/CONSENT-MODEL-2026-10-30.md, "Web call panel") ----------------------

/** The word on the capture controls. */
export type CaptureNoun = 'transcription' | 'recording';

/**
 * "transcription" when the call is transcripts only (transcriptsOnly()) and, because transcripts only is the default
 * capture, while the recorder reports nothing yet; "recording" only once the recorder itself reports audio (recording,
 * paused, processing, saved or failed). A label choice from server observations; it never claims a state.
 */
export function captureNoun(c: Pick<CallCapture, 'observed_state'>, transcription: Call['transcription_state'] | null | undefined): CaptureNoun {
  if (transcriptsOnly({ recording_state: c.observed_state, transcription_state: transcription })) return 'transcription';
  return c.observed_state === 'off' || c.observed_state === 'awaiting_consent' ? 'transcription' : 'recording';
}

/** Button label for a capture control, for example "Pause transcription" or "Stop recording". */
export function captureLabel(action: 'start' | 'pause' | 'resume' | 'stop', noun: CaptureNoun): string {
  return `${{ start: 'Start', pause: 'Pause', resume: 'Resume', stop: 'Stop' }[action]} ${noun}`;
}

/** Whether agents may pause and stop capture on this call. An older API sends nothing (and an unknown value is not
 * 'none'): agents keep the controls. */
export function agentControlOf(c: Pick<CallCapture, 'agent_control'>): AgentControl {
  return c.agent_control === 'none' ? 'none' : 'pause_stop';
}

/** The capture view's privacy reminder, or null when the API sends none. */
export function captureReminderOf(c: Pick<CallCapture, 'capture_reminder'>): string | null {
  return c.capture_reminder?.trim() || null;
}

/** Shown in place of Pause, Resume and Stop when the workspace turned agent control off. */
export const AGENT_CONTROL_OFF_TEXT = 'Pausing is turned off for this workspace.';
export const AUTOMATIC_CAPTURE_TEXT = `Automatic for this call. ${AGENT_CONTROL_OFF_TEXT}`;

export type CaptureUnavailable =
  | 'route_unavailable' // this server does not offer the media routes yet
  | 'external_call' // PSTN/external consent is not configured
  | 'not_participant'
  | 'not_permitted'
  | 'session'; // signed out or access blocked

export interface CaptureSnapshot {
  status: 'idle' | 'loading' | 'ready' | 'unavailable' | 'error';
  capture: CallCapture | null;
  unavailable: CaptureUnavailable | null;
  /** A plain-language problem from the last action or refresh (never raw protocol text). */
  problem: string | null;
  pendingConsent: 'grant' | 'withdraw' | null;
  pendingCapture: 'start' | 'pause' | 'resume' | 'stop' | null;
  /** True while a request's outcome is unknown and is being reconciled by reading state. */
  checking: boolean;
  /** Bounded settle polling gave up: the phone system has not confirmed yet. */
  stale: boolean;
}

/** Audit reasons describe exactly what the user clicked; they never claim anyone else's agreement. */
export const CAPTURE_REASONS = {
  grant: 'Clicked "Allow for this call" (recording and transcription) in the Sidetone call panel',
  withdraw: 'Clicked "Withdraw my consent" (recording and transcription) in the Sidetone call panel',
  start: 'Clicked "Start recording" in the Sidetone call panel',
} as const;

/** The audit reason for a capture control: the exact label of the button that was clicked. */
export function captureReason(action: 'start' | 'pause' | 'resume' | 'stop', noun: CaptureNoun): string {
  return action === 'start' ? CAPTURE_REASONS.start : `Clicked "${captureLabel(action, noun)}" in the Sidetone call panel`;
}

const SETTLE_DELAYS_MS = [1000, 2000, 4000, 5000];
const SETTLE_LIMIT_MS = 120_000;
const BACKGROUND_POLL_MS = 15_000;

function problemFor(code: string, agentControl: AgentControl = 'pause_stop'): string {
  switch (code) {
    case 'version_conflict':
    case 'capture_state_conflict':
      return 'Recording on this call changed in the meantime. The latest state is shown; try again if you still want to.';
    case 'all_participant_consent_required':
      return 'Everyone on the call must agree before recording can start.';
    case 'capture_policy_unverified':
      return 'Recording and transcription aren’t set up for this workspace.';
    case 'capture_controller_unavailable':
    case 'capture_unavailable':
      return 'Recording can’t start: workspace authorization or recorder readiness is unavailable. You can still pause or withdraw consent.';
    case 'capture_privacy_unconfirmed':
      return 'The previous stop has not been confirmed. Check the recording status before starting again.';
    case 'call_not_active':
      return 'Recording consent can change only while the call is in progress.';
    case 'call_not_ready':
      return 'The call isn’t fully connected yet. Try again in a moment.';
    case 'participant_required':
      return 'Only people on this call can change its recording consent.';
    case 'permission_denied':
      return agentControl === 'none' ? AGENT_CONTROL_OFF_TEXT : 'Your role can’t start or pause recording.';
    default:
      return 'That didn’t go through. Try again.';
  }
}

export interface CaptureControllerOptions {
  api: Pick<ApiClient, 'request'>;
  callId: string;
  /** The signed-in user; only this user's own participant consent is ever changed. */
  selfUserId: string;
  access?: MediaAccess;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
  now?: () => number;
}

export class CaptureController {
  private disclosurePending=false;
  private snapshot: CaptureSnapshot = { status: 'idle', capture: null, unavailable: null, problem: null, pendingConsent: null, pendingCapture: null, checking: false, stale: false };
  private readonly listeners = new Set<() => void>();
  private seq = 0;
  private appliedSeq = 0;
  private refreshController: AbortController | null = null;
  private refreshAgain = false;
  private timer: unknown = null;
  private settleStartedAt: number | null = null;
  private settleAttempt = 0;
  private active = false;
  private disposed = false;
  private unsubscribeAccess: (() => void) | null = null;
  private accessEpoch = 0;
  /** The call row's transcription state (observeTranscription): tells a transcripts-only capture from a start still settling. */
  private transcription: Call['transcription_state'] | null = null;
  private readonly setTimer: (fn: () => void, ms: number) => unknown;
  private readonly clearTimer: (handle: unknown) => void;
  private readonly now: () => number;
  private readonly path: string;

  constructor(private readonly options: CaptureControllerOptions) {
    this.setTimer = options.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
    this.clearTimer = options.clearTimer ?? (h => clearTimeout(h as ReturnType<typeof setTimeout>));
    this.now = options.now ?? Date.now;
    this.path = `/calls/${encodeURIComponent(options.callId)}/media`;
  }

  getSnapshot = (): CaptureSnapshot => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /** Start reading state (call is live and the band is shown). */
  activate(): void {
    if (this.active || this.disposed) return;
    this.active = true;
    const access = this.options.access;
    if (access) {
      this.accessEpoch = access.getSnapshot().epoch;
      this.unsubscribeAccess = access.subscribe(() => this.onAccess());
    }
    void this.refresh();
  }

  /** Stop all timers and requests; cached state is cleared (the call ended or the view went away). */
  dispose(): void {
    this.disposed = true;
    this.active = false;
    this.unsubscribeAccess?.();
    this.unsubscribeAccess = null;
    this.cancelTimer();
    this.refreshController?.abort();
    this.refreshController = null;
    this.listeners.clear();
  }

  /** An authoritative event (call_capture or call) for this call: read the current state. */
  notify(): void {
    if (!this.active) return;
    this.settleStartedAt = null;
    void this.refresh();
  }

  /** The call's observed transcription state. With capture requested and the recorder off, a running transcription is the
   * settled transcripts-only state, not a start still waiting for a recorder: settle polling stops and no stale prompt shows. */
  observeTranscription(state: Call['transcription_state']): void {
    if (this.transcription === state) return;
    this.transcription = state;
    if (this.active && !this.disposed) this.schedule();
  }

  /** "Check again" after settle polling gave up. */
  checkAgain(): void {
    this.settleStartedAt = null;
    this.set({ stale: false });
    void this.refresh();
  }

  async refresh(): Promise<void> {
    if (!this.active || this.disposed) return;
    if (this.refreshController) {
      this.refreshAgain = true;
      return;
    }
    const seq = ++this.seq;
    const controller = new AbortController();
    this.refreshController = controller;
    if (this.snapshot.status === 'idle') this.set({ status: 'loading' });
    try {
      const env = await this.options.api.request<DataEnvelope<CallCapture>>(this.path, { signal: controller.signal });
      if (this.refreshController !== controller) return;
      this.apply(env.data, seq);
      this.set({ checking: false });
    } catch (error) {
      if (this.refreshController !== controller || isAbortError(error)) return;
      this.onReadError(error);
    } finally {
      if (this.refreshController === controller) this.refreshController = null;
    }
    if (this.refreshAgain && this.active) {
      this.refreshAgain = false;
      void this.refresh();
      return;
    }
    this.schedule();
  }

  async requestDisclosure(): Promise<void> {
    const c=this.snapshot.capture;if(!c?.remote?.can_request||this.disclosurePending||this.disposed)return;
    this.disclosurePending=true;const seq=++this.seq;this.set({checking:true,problem:null});
    try{const env=await this.options.api.request<DataEnvelope<CallCapture>>(`${this.path}/disclosure`,{method:'POST',body:JSON.stringify({expected_version:c.version,reason:'Requested the configured owned disclosure for the current external party.'})});this.apply(env.data,seq);}
    catch(error){this.onWriteError(error);}finally{this.disclosurePending=false;this.set({checking:false});this.schedule();}
  }

  grant(): Promise<void> {
    return this.consent(true);
  }

  withdraw(): Promise<void> {
    return this.consent(false);
  }

  /** Own consent only: the server derives the participant; no user or participant ID is sent. */
  private async consent(granted: boolean): Promise<void> {
    const c = this.snapshot.capture;
    const own = c?.participants.find(p => p.user_id === this.options.selfUserId) ?? null;
    if (!c || !own || this.snapshot.pendingConsent || !c.policy_version || this.disposed) return;
    if (granted ? own.consent || !c.policy_verified || !(c.capability.status === 'enabled' && c.capability.allowed) : !own.consent) return;
    const seq = ++this.seq;
    this.set({ pendingConsent: granted ? 'grant' : 'withdraw', problem: null });
    try {
      const env = await this.options.api.request<DataEnvelope<CallCapture>>(`${this.path}/consent`, {
        method: 'POST',
        body: JSON.stringify({ granted, policy_version: c.policy_version, expected_version: own.version, reason: granted ? CAPTURE_REASONS.grant : CAPTURE_REASONS.withdraw }),
      });
      this.apply(env.data, seq);
    } catch (error) {
      this.onWriteError(error);
    } finally {
      this.set({ pendingConsent: null });
      this.settleStartedAt = null;
      this.schedule();
    }
  }

  start(): Promise<void> {
    return this.capture('start');
  }

  pause(): Promise<void> {
    return this.capture('pause');
  }

  resume(): Promise<void> {
    return this.capture('resume');
  }

  stop(): Promise<void> {return this.capture('stop');}

  private async capture(action: 'start' | 'pause' | 'resume' | 'stop'): Promise<void> {
    const c = this.snapshot.capture;
    if (!c || this.snapshot.pendingCapture || this.disposed) return;
    const seq = ++this.seq;
    this.set({ pendingCapture: action, problem: null });
    try {
      const env = await this.options.api.request<DataEnvelope<CallCapture>>(`${this.path}/${action==='start'&&c.number_policy?.mode==='ask'?'record':'capture'}`, {
        method: 'POST',
        body: JSON.stringify(action==='start'&&c.number_policy?.mode==='ask'?{expected_version:c.version,policy_version:c.policy_version,question_text:c.number_policy.question_text}:{ action, expected_version: c.version, reason: captureReason(action, captureNoun(c, this.transcription)) }),
      });
      this.apply(env.data, seq);
    } catch (error) {
      this.onWriteError(error);
    } finally {
      this.set({ pendingCapture: null });
      this.settleStartedAt = null;
      this.schedule();
    }
  }

  /**
   * Version guard: a higher capture `version` always wins; an equal version is accepted only
   * from a request that started later. A late or stale response never overwrites newer state.
   */
  private apply(next: CallCapture, seq: number): void {
    const current = this.snapshot.capture;
    if (current && (next.version < current.version || (next.version === current.version && seq < this.appliedSeq))) return;
    this.appliedSeq = Math.max(this.appliedSeq, seq);
    this.set({ status: 'ready', capture: next, unavailable: null });
  }

  private onReadError(error: unknown): void {
    if (!isApiRequestError(error)) {
      this.set({ problem: 'Couldn’t check recording on this call. Retrying.' });
      return;
    }
    const unavailable = this.unavailableFor(error.status, error.code);
    if (unavailable) {
      // Authorization or availability was lost: drop cached state at once.
      this.cancelTimer();
      this.set({ status: 'unavailable', capture: null, unavailable, problem: null });
      return;
    }
    this.set({ status: this.snapshot.capture ? 'ready' : 'error', problem: error.status === 0 ? 'Couldn’t reach the server. Retrying.' : 'Couldn’t check recording on this call. Retrying.' });
  }

  private onWriteError(error: unknown): void {
    if (isApiRequestError(error)) {
      if (error.outcomeUnknown) {
        // The request may have been applied: read the authoritative state; never re-send.
        this.set({ checking: true, problem: null });
        void this.refresh();
        return;
      }
      const unavailable = this.unavailableFor(error.status, error.code);
      if (unavailable) {
        this.cancelTimer();
        this.set({ status: 'unavailable', capture: null, unavailable, problem: null });
        return;
      }
      this.set({ problem: problemFor(error.code, this.snapshot.capture ? agentControlOf(this.snapshot.capture) : 'pause_stop') });
      if (error.status === 409 || error.status === 422) void this.refresh();
      return;
    }
    this.set({ problem: problemFor('') });
  }

  private unavailableFor(status: number, code: string): CaptureUnavailable | null {
    if (status === 401) return 'session';
    if (status === 404 && code === 'not_found') return 'route_unavailable';
    if (status === 503 && code === 'external_consent_unconfigured') return 'external_call';
    if (status === 403 && code === 'participant_required') return 'not_participant';
    if (status === 403 && (code === 'mfa_required' || code === 'tenant_required')) return 'session';
    return null;
  }

  private onAccess(): void {
    const access = this.options.access;
    if (!access) return;
    const snap = access.getSnapshot();
    if (snap.epoch === this.accessEpoch) return;
    this.accessEpoch = snap.epoch;
    if (snap.state === 'blocked') {
      this.refreshController?.abort();
      this.refreshController = null;
      this.cancelTimer();
      this.set({ status: 'unavailable', capture: null, unavailable: 'session', problem: null });
      return;
    }
    if (snap.state === 'ready') {
      this.set({ status: this.snapshot.capture ? 'ready' : 'idle', unavailable: null });
      void this.refresh();
    }
  }

  /** Settling: an unconfirmed privacy barrier or requested state not yet observed. */
  private settling(c: CallCapture): boolean {
    if (c.privacy_ack === 'pending') return true;
    if (c.requested_state === 'recording' && c.observed_state !== 'recording') return c.observed_state !== 'failed' && !capturingTranscriptsOnly(c, this.transcription);
    return false;
  }

  private schedule(): void {
    this.cancelTimer();
    if (!this.active || this.disposed) return;
    const c = this.snapshot.capture;
    if (this.snapshot.status === 'unavailable') return;
    let delay = BACKGROUND_POLL_MS;
    if (c && this.settling(c)) {
      const now = this.now();
      if (this.settleStartedAt === null) {
        this.settleStartedAt = now;
        this.settleAttempt = 0;
      }
      if (now - this.settleStartedAt > SETTLE_LIMIT_MS) {
        this.set({ stale: true });
        delay = BACKGROUND_POLL_MS;
      } else {
        delay = SETTLE_DELAYS_MS[Math.min(this.settleAttempt, SETTLE_DELAYS_MS.length - 1)] ?? 5000;
        this.settleAttempt += 1;
      }
    } else {
      this.settleStartedAt = null;
      if (this.snapshot.stale) this.set({ stale: false });
    }
    this.timer = this.setTimer(() => {
      this.timer = null;
      void this.refresh();
    }, delay);
  }

  private cancelTimer(): void {
    if (this.timer !== null) this.clearTimer(this.timer);
    this.timer = null;
  }

  private set(patch: Partial<CaptureSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...patch };
    for (const l of [...this.listeners]) l();
  }
}

/** One controller per call, shared by every mounted view of it (dock card and call detail). */
export class CaptureRegistry {
  private readonly entries = new Map<string, { controller: CaptureController; refs: number }>();

  constructor(private readonly make: (callId: string) => CaptureController) {}

  acquire(callId: string): CaptureController {
    const id = callId.toLowerCase();
    let entry = this.entries.get(id);
    if (!entry) {
      entry = { controller: this.make(id), refs: 0 };
      this.entries.set(id, entry);
      entry.controller.activate();
    }
    entry.refs += 1;
    return entry.controller;
  }

  release(callId: string): void {
    const id = callId.toLowerCase();
    const entry = this.entries.get(id);
    if (!entry) return;
    entry.refs -= 1;
    if (entry.refs <= 0) {
      entry.controller.dispose();
      this.entries.delete(id);
    }
  }

  notify(callId: string): void {
    this.entries.get(callId.toLowerCase())?.controller.notify();
  }

  disposeAll(): void {
    for (const { controller } of this.entries.values()) controller.dispose();
    this.entries.clear();
  }
}

// ---- View model --------------------------------------------------------------------------------

export type CaptureTone = 'neutral' | 'live' | 'warn' | 'end' | 'hold';

export interface CaptureParticipantView {
  participantId: string;
  name: string;
  isSelf: boolean;
  agreed: boolean;
}

export interface CaptureActionView {
  visible: boolean;
  enabled: boolean;
  reason: string | null;
}

export interface CaptureView {
  status: { label: string; detail: string | null; tone: CaptureTone };
  /** Capture is on and transcribing with no recorder (transcripts only): never a failure or a pending start. */
  transcriptsOnly?: boolean;
  /** The word on Pause, Resume and Stop (captureNoun()). */
  noun: CaptureNoun;
  /** 'none': the workspace turned agent pause and stop off; those controls are hidden. */
  agentControl: AgentControl;
  /** The privacy reminder from the API, shown at the top of the band; null when an older API sends none. */
  reminder: string | null;
  recorder: string;
  transcription: { text: string; tone: 'neutral' | 'live' | 'warn' };
  archive: string;
  participants: CaptureParticipantView[];
  agreedCount: number;
  self: { present: boolean; agreed: boolean; version: number };
  grant: CaptureActionView;
  withdraw: CaptureActionView;
  start: CaptureActionView;
  pause: CaptureActionView;
  resume: CaptureActionView;
}

const hidden: CaptureActionView = { visible: false, enabled: false, reason: null };

/** Capture requested and running with no recorder (transcripts only): the recorder reports off while the call's transcription
 * has started. Both are server observations; an accepted request alone never counts. */
export function capturingTranscriptsOnly(c: Pick<CallCapture, 'requested_state' | 'observed_state'>, transcription: Call['transcription_state'] | null | undefined): boolean {
  return c.requested_state === 'recording' && transcriptsOnly({ recording_state: c.observed_state, transcription_state: transcription });
}

/**
 * Status and controls from server truth. "Recording" only when the call's observed recording
 * state says so; "Stopped"/"Paused" only after the privacy barrier is confirmed.
 */
export function captureView(c: CallCapture, selfUserId: string, call: Pick<Call, 'state'> & Partial<Pick<Call, 'transcription_state'>>, names: (userId: string) => string | null, pending: Pick<CaptureSnapshot, 'pendingConsent' | 'pendingCapture'>): CaptureView {
  const participants = c.participants.map(p => {
    const isSelf = p.user_id === selfUserId;
    return { participantId: p.participant_id, name: isSelf ? 'You' : p.user_id ? names(p.user_id) ?? 'A teammate (name not shared with you)' : 'Other party', isSelf, agreed: p.consent };
  });
  const own = c.participants.find(p => p.user_id === selfUserId) ?? null;
  const agreedCount = participants.filter(p => p.agreed).length;
  const live = call.state === 'active' || call.state === 'held';
  const verified = c.policy_verified && !!c.policy_version;
  const captureReady = c.capability.status === 'enabled' && c.capability.allowed;
  const transcribing = capturingTranscriptsOnly(c, call.transcription_state);
  const noun = captureNoun(c, call.transcription_state);
  const agentControl = agentControlOf(c);
  const controlsOff = agentControl === 'none';

  let status: CaptureView['status'];
  if (c.privacy_ack === 'pending') status = c.requested_state === 'paused'
    ? { label: 'Pausing…', detail: 'Pause requested. Waiting for the capture privacy acknowledgement; transcription may still be delayed.', tone: 'warn' }
    : { label: 'Stopping…', detail: 'Recording is no longer permitted. Waiting for the capture privacy acknowledgement; stopping is not confirmed yet.', tone: 'warn' };
  else if (c.privacy_ack === 'failed') status = { label: 'Stop not confirmed', detail: 'The phone system couldn’t confirm the capture privacy barrier. Recording stays not permitted. Remote transcription cancellation is not confirmed. Ask an administrator to check the phone system.', tone: 'end' };
  else if (c.observed_state === 'failed') status = { label: 'Recording failed', detail: 'The recorder reported a failure. Check the call and transcription states separately.', tone: 'warn' };
  else if (c.observed_state === 'recording') status = c.requested_state === 'recording'
    ? { label: 'Recording', detail: 'The recorder is active. Live transcription is reported separately below.', tone: 'end' }
    : { label: 'Stopping…', detail: 'Waiting for the recorder to confirm.', tone: 'warn' };
  else if (c.observed_state === 'processing') status = { label: 'Recording processing', detail: 'Recorded audio is processing. Archive availability is reported separately.', tone: 'neutral' };
  else if (c.observed_state === 'ready') status = { label: 'Recording saved', detail: 'The recorder reports saved audio. Playback availability and access are checked separately.', tone: 'neutral' };
  else if (c.observed_state === 'paused' && c.requested_state === 'recording') status = { label: 'Recorder paused', detail: 'The recorder reports paused. Transcription and privacy acknowledgement are separate.', tone: 'hold' };
  // Only a running (or finished) transcription says "Transcribing"; pending or delayed means nothing is being captured yet.
  else if (transcribing && (call.transcription_state === 'live' || call.transcription_state === 'complete')) status = { label: 'Transcribing', detail: 'Live transcription is on for this call. Call audio is not recorded (transcripts only).', tone: 'live' };
  else if (transcribing && call.transcription_state === 'delayed') status = { label: 'Transcription delayed', detail: 'Capture is on but live transcription is not running right now. Call audio is not recorded (transcripts only).', tone: 'warn' };
  else if (transcribing) status = { label: 'Starting transcription…', detail: 'Capture is on. Waiting for live transcription to start. Call audio is not recorded (transcripts only).', tone: 'neutral' };
  else if (c.requested_state === 'recording') status = { label: 'Starting…', detail: 'Waiting for the recorder to confirm it has started. Nothing is claimed as recorded until it does.', tone: 'neutral' };
  else if (c.requested_state === 'paused') status = c.privacy_ack === 'confirmed'
    ? { label: 'Paused', detail: 'Capture pause confirmed. This does not erase text already received.', tone: 'hold' }
    : { label: 'Pausing…', detail: 'Waiting for the capture privacy acknowledgement. Pause is not confirmed yet.', tone: 'warn' };
  else if (c.privacy_ack === 'confirmed') status = { label: 'Stopped', detail: 'Capture stop confirmed. This does not erase text already received.', tone: 'neutral' };
  else if (!c.all_consented) status = { label: 'Off', detail: `${captureReady ? 'Recording is configured; request recording after everyone consents. ' : ''}Waiting for everyone to agree (${agreedCount} of ${participants.length}).`, tone: 'neutral' };
  else status = { label: 'Off', detail: 'Everyone has agreed. Recording starts only when someone on the call presses Start.', tone: 'neutral' };

  const pendingConsent = pending.pendingConsent !== null;
  const pendingCapture = pending.pendingCapture !== null;
  const notLive = live ? null : 'Recording consent can change only while the call is in progress.';

  const grant: CaptureActionView = !own || own.consent ? hidden : {
    visible: true,
    enabled: verified && captureReady && live && !pendingConsent,
    reason: !verified ? 'Recording and transcription aren’t set up for this workspace.' : !captureReady ? 'Recording can’t start yet: workspace authorization or recorder readiness is unavailable.' : notLive ?? (pendingConsent ? 'Saving…' : null),
  };
  // Withdrawal is never blocked by a pending capture command.
  const withdraw: CaptureActionView = !own || !own.consent ? hidden : {
    visible: true,
    enabled: live && !pendingConsent,
    reason: notLive ?? (pendingConsent ? 'Saving…' : null),
  };

  const controlReason = (): string | null => {
    if (!verified) return 'Recording and transcription aren’t set up for this workspace.';
    if (!captureReady) return 'Recording can’t start yet: workspace authorization or recorder readiness is unavailable.';
    // can_control stays the start authority; agent_control only removes pause and stop (enforced by the API too).
    if (!c.can_control) return 'Your role can’t start recording.';
    if (!c.all_consented) return 'Everyone on the call must agree first.';
    if (c.privacy_ack === 'pending' || c.privacy_ack === 'failed') return 'Waiting for the previous stop to be confirmed.';
    if (!live) return notLive;
    if (pendingCapture) return 'Waiting for the phone system…';
    return null;
  };
  const start: CaptureActionView = c.requested_state !== 'off' ? hidden : { visible: !!own, enabled: false, reason: null };
  if (start.visible) {
    start.reason = controlReason();
    start.enabled = start.reason === null;
  }
  // Agent control turned off by the workspace: Pause and Resume are hidden entirely (withdrawal is unaffected).
  const turnedOff: CaptureActionView = { visible: false, enabled: false, reason: AGENT_CONTROL_OFF_TEXT };
  const resume: CaptureActionView = c.requested_state !== 'paused' ? hidden : controlsOff ? turnedOff : { visible: !!own, enabled: false, reason: null };
  if (resume.visible) {
    resume.reason = controlReason();
    resume.enabled = resume.reason === null;
  }
  // Pausing (stopping capture) needs call control but never the capability or everyone's consent.
  const pause: CaptureActionView = c.requested_state !== 'recording' ? hidden : controlsOff ? turnedOff : {
    visible: !!own,
    enabled: c.can_control && live && !pendingCapture,
    reason: !c.can_control ? `Your role can’t pause ${noun}. Withdrawing your consent stops it.` : notLive ?? (pendingCapture ? 'Waiting for the phone system…' : null),
  };

  return { status, transcriptsOnly: transcribing, noun, agentControl, reminder: captureReminderOf(c), recorder: recorderObservation(c.observed_state), transcription: transcriptStateText(call.transcription_state, captureReady), archive: transcribing ? `${TRANSCRIPTS_ONLY_TEXT}.` : captureArchiveText(c.observed_state), participants, agreedCount, self: { present: !!own, agreed: !!own?.consent, version: own?.version ?? 0 }, grant, withdraw, start, pause, resume };
}

export function unavailableText(reason: CaptureUnavailable): string {
  switch (reason) {
    case 'route_unavailable':
      return 'Recording consent isn’t available on this server yet.';
    case 'external_call':
      return 'Recording isn’t available for calls with outside numbers.';
    case 'not_participant':
      return 'Only people on this call can see or change its recording consent.';
    case 'not_permitted':
      return 'Your role can’t see recording consent for this call.';
    default:
      return 'Sign in again to see recording consent for this call.';
  }
}
